import { useMemo, useState } from "react";
import { Button, TimePicker, cn } from "@carry/ui";
import { BottomBar } from "../components/BottomBar";
import { Header } from "../components/Header";
import type { CreateDriverDelayReportInput } from "../hooks";
import type { DriverStop } from "../lib/normalize";

/**
 * F14 Slice 2: 遅延報告フォーム.
 * delay_reason / eta_type / eta_at / impact_note を入力し、親 (App) で
 * createDriverDelayReport を呼ぶ。送信成功で admin/staff に LINE Push が
 * 発火する。
 */
const REASON_OPTIONS: Array<{
  value: CreateDriverDelayReportInput["delay_reason"];
  label: string;
}> = [
  { value: "traffic", label: "渋滞" },
  { value: "production_delay", label: "製造遅延" },
  { value: "previous_case_delay", label: "前案件の遅延に伴い" },
  { value: "other", label: "その他" },
];

export function DelayReport({
  stop,
  acting,
  error,
  onBack,
  onSubmit,
}: {
  stop: DriverStop;
  acting: boolean;
  error: string | null;
  onBack: () => void;
  onSubmit: (input: CreateDriverDelayReportInput) => void;
}) {
  const [reason, setReason] = useState<CreateDriverDelayReportInput["delay_reason"]>("traffic");
  const [etaMode, setEtaMode] = useState<"unknown" | "specified">("unknown");
  const [etaTime, setEtaTime] = useState<string>(() => {
    // 既存 stop.time が "HH:MM" の場合は 30 分後を初期値に
    const parts = stop.time?.split(":") ?? [];
    const h = Number(parts[0] ?? "0");
    const m = Number(parts[1] ?? "0") + 30;
    const total = h * 60 + m;
    const hh = String(Math.floor(total / 60) % 24).padStart(2, "0");
    const mm = String(total % 60).padStart(2, "0");
    return `${hh}:${mm}`;
  });
  const [impactNote, setImpactNote] = useState("");

  const todayJst = useMemo(() => {
    const now = new Date();
    const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    return jst.toISOString().slice(0, 10);
  }, []);

  function buildPayload(): CreateDriverDelayReportInput {
    const base: CreateDriverDelayReportInput = {
      delay_reason: reason,
      eta_type: etaMode,
    };
    if (etaMode === "specified") {
      base.eta_at = `${todayJst}T${etaTime}:00+09:00`;
    }
    const trimmed = impactNote.trim();
    if (trimmed.length > 0) base.impact_note = trimmed;
    return base;
  }

  const overLimit = impactNote.length > 1000;

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header title="遅延報告" sub={stop.name} showBack onBack={onBack} />
      <div className="px-3 py-3.5">
        <div className="mb-3 rounded-xl bg-[var(--bg-raised)] p-3.5 shadow-sm">
          <div className="mb-1 text-[length:var(--fs-xs)] text-[var(--ink-3)]">対象の配送</div>
          <div className="mb-0.5 text-[length:var(--fs-md)] font-semibold">{stop.name}</div>
          <div className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">予定到着 {stop.time}</div>
          {stop.clientPhone && (
            <a
              href={`tel:${stop.clientPhone}`}
              className="mt-2.5 flex min-h-[var(--row-h)] items-center justify-center gap-1.5 rounded-lg border border-[var(--info)] px-4 py-2.5 text-[length:var(--fs-sm)] font-semibold text-[var(--info)]"
            >
              取引先に電話 ({stop.clientPhone})
            </a>
          )}
        </div>

        <div className="mb-3 rounded-xl bg-[var(--bg-raised)] p-3.5 shadow-sm">
          <div className="mb-2.5 text-[length:var(--fs-sm)] font-semibold">遅延理由</div>
          {REASON_OPTIONS.map((r) => {
            const sel = r.value === reason;
            return (
              <label
                key={r.value}
                className={cn(
                  "mb-1.5 flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2.5",
                  sel
                    ? "border-[1.5px] border-[var(--ok)] bg-[var(--st-done-bg)]"
                    : "border border-transparent bg-[var(--bg-sunken)]",
                )}
              >
                <input
                  type="radio"
                  name="delay-reason"
                  checked={sel}
                  onChange={() => setReason(r.value)}
                  className="hidden"
                />
                <div
                  className={cn(
                    "h-[18px] w-[18px] flex-shrink-0 rounded-full bg-[var(--bg-raised)]",
                    sel
                      ? "border-[5px] border-[var(--ok)]"
                      : "border-[1.5px] border-[var(--ink-4)]",
                  )}
                />
                <span
                  className={cn(
                    "text-[length:var(--fs-sm)]",
                    sel ? "font-semibold" : "font-normal",
                  )}
                >
                  {r.label}
                </span>
              </label>
            );
          })}
        </div>

        <div className="mb-3 rounded-xl bg-[var(--bg-raised)] p-3.5 shadow-sm">
          <div className="mb-2.5 text-[length:var(--fs-sm)] font-semibold">到着見込み</div>
          <div className="mb-2 flex gap-2">
            {(["unknown", "specified"] as const).map((m) => {
              const sel = etaMode === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setEtaMode(m)}
                  className={cn(
                    "flex-1 rounded-lg border px-3 py-2 text-[length:var(--fs-sm)] font-medium",
                    sel
                      ? "border-[var(--ok)] bg-[var(--st-done-bg)] text-[var(--ok)]"
                      : "border-[var(--rule-soft)] bg-[var(--bg-sunken)] text-[var(--ink-2)]",
                  )}
                >
                  {m === "unknown" ? "未定" : "時間指定"}
                </button>
              );
            })}
          </div>
          {etaMode === "specified" && (
            <div>
              <TimePicker
                value={etaTime}
                onChange={setEtaTime}
                disabled={acting}
                aria-label="到着見込時刻"
                className="w-full justify-center"
              />
              <div className="mt-1 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
                本日 ({todayJst}) の到着見込時刻
              </div>
            </div>
          )}
        </div>

        <div className="mb-3 rounded-xl bg-[var(--bg-raised)] p-3.5 shadow-sm">
          <div className="mb-2.5 text-[length:var(--fs-sm)] font-semibold">
            その後の納品への影響 (任意)
          </div>
          <textarea
            value={impactNote}
            onChange={(e) => setImpactNote(e.target.value)}
            rows={3}
            placeholder="例: 次案件 (佐藤電機) の到着が 15 分遅れる見込み"
            className="w-full resize-none rounded-lg border border-[var(--rule-soft)] bg-[var(--bg-sunken)] px-3 py-2 text-[length:var(--fs-sm)]"
          />
          <div
            className={cn(
              "mt-1 text-right text-[length:var(--fs-xs)]",
              overLimit ? "text-[var(--danger)]" : "text-[var(--ink-3)]",
            )}
          >
            {impactNote.length} / 1000
          </div>
        </div>

        {error && (
          <div className="rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
            ⚠ {error}
          </div>
        )}
      </div>

      <BottomBar>
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={acting}
          className="h-auto min-h-[var(--row-h)] px-4 py-3 text-[length:var(--fs-sm)] text-[var(--ink-3)]"
        >
          キャンセル
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={() => onSubmit(buildPayload())}
          disabled={acting || overLimit}
          className={cn(
            "h-auto min-h-[var(--row-h)] flex-1 px-4 py-3 text-[length:var(--fs-md)] font-semibold",
            acting && "cursor-wait opacity-70",
          )}
        >
          {acting ? "送信中…" : "遅延を報告"}
        </Button>
      </BottomBar>
    </div>
  );
}
